/**
 * SessionChargePage — 테이블 세션 전체 결제 전용 페이지 (#661).
 *
 * 기존 ChargeSessionModal(팝업)이 주문내역을 가려 "주문 내역을 보면서 팁/포인트를 입력"하는
 * 흐름이 불가능했다(2026-06-10 사용자 보고). 모달을 전용 페이지로 전환해 한 화면에서
 * **주문한 메뉴 내역(주문별 라인아이템) + 합계 + 팁/포인트 + 카드 입력 + '전체 결제하기'** 를 제공한다.
 *
 * ## 구성
 *   - 데이터: `useSessionUnpaidPostpay`(공유 훅, enabled=true) — 진행바/시트/주문내역과 단일 출처.
 *     주문별 라인은 PR #656(#654)이 머지한 `orders`(주문별 구조)를 사용한다.
 *   - 주문별 섹션: 주문번호 + 라인아이템(SessionOrderLine 공유) + 소계(서버 권위 `order.total`).
 *   - 합계: 서버 권위 `total_amount` (FE 재합산 금지).
 *   - 결제: `SessionChargePanel` — 팁/포인트(단건 한정 #654 adjustmentOrderId 게이팅) +
 *     0원 분기 + 카드 폼 + Bloomin 3DS challenge redirect (모달 로직 이관).
 *
 * ## 진입점 (3곳 — 전부 정상 라우트 push)
 *   - 주문내역(OrderHistoryPage) 배너 "전체 결제"
 *   - SessionOrdersSheet "전체 결제하기" (주문내역 배너 시트 / 메뉴 화면 진행바 시트)
 *   - 메뉴 화면 상단 PAY(SessionProgressContainer.openCharge) / 하단 진행바 "결제하기"
 *
 * ## 뒤로가기 (PR #622/#621 /cart 모달 히스토리 함정 재발 금지)
 *   본 페이지는 모달 히스토리 조작 없이 **정상 라우트 push** 로 진입한다 — 뒤로가기(navigate(-1))
 *   는 직전 화면(주문내역/메뉴)으로 자연 복귀한다. 3DS challenge redirect 는 페이지를 떠나는
 *   기존 구조 그대로다(복귀는 admin 콜백 → /payment/result).
 *
 * ## fail-closed (직접 URL 진입 방어)
 *   `hasPayableSessionLines` SSOT 게이트 — 조회 완료 후 미결제 없음 / 빈 라인(계약 위반) /
 *   토큰 부재 / 조회 실패면 주문내역(/order-history)으로 redirect 한다.
 *
 * @see src/components/payment/SessionChargePanel.tsx (결제 로직 — 모달 이관)
 * @see src/hooks/useSessionUnpaidPostpay.ts (데이터 단일 출처)
 * @see src/components/session/SessionOrdersSheet.tsx (SessionOrderLine 렌더 공유)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SessionChargePanel } from '@/components/payment/SessionChargePanel';
import { buildSessionChargeLabels } from '@/components/payment/sessionChargeLabels';
import {
  BillReceiptViewer,
  ComensalAccessPanel,
  SessionOrderLine,
  SplitPaymentSection,
} from '@/components/session';
import { getSplitPaymentLabels } from '@/i18n/splitPayment';
import { hasPayableSessionLines, useSessionUnpaidPostpay } from '@/hooks/useSessionUnpaidPostpay';
import { usePaymentMethodCombinations } from '@/hooks/usePaymentMethodCombinations';
import { useStoreAcceptedPaymentMethods } from '@/hooks/useStoreAcceptedPaymentMethods';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import { paymentTranslations, payNowTranslations } from '@/i18n/paymentModal';
import { ROUTES } from '@/config/routes';
import { formatCurrency } from '@/utils/currency';
import { computeAvailableCombinations } from '@/lib/payments/combinations';
import type { TableSessionPaymentRequestMethod } from '@/lib/api/payments';
import { useComensalSession } from '@/hooks/useComensalSession';
import { useSessionBills } from '@/hooks/useSessionBills';
import { getTableSessionFromStorage } from '@/lib/tableContext';
import { getComensalSessionLabels } from '@/i18n/comensalSession';
import { getSplitClaimLabels } from '@/i18n/splitClaims';
import { readSplitPaymentAvailable } from '@/lib/comensalAccess';
import {
  readPendingBillReceipt,
  subscribePendingBillReceipt,
} from '@/lib/billReceiptContext';
import { SESSION_BILLS_CHANGED_EVENT } from '@/lib/sessionStorageKeys';

const CARD_PLATFORM_POSTPAY_CODE = 'card_platform_postpay';
const CASH_MANUAL_POSTPAY_CODE = 'cash_manual_postpay';
const CARD_POS_POSTPAY_CODE = 'card_pos_postpay';

export function SessionChargePage() {
  const { locale, currency } = useApp();
  const { isLoggedIn } = useAuth();
  const { refetch, guestCredentials } = useOrderHistory();
  const navigate = useNavigate();
  const location = useLocation();

  // 회원이면 guest_password 미전송 (GET/POST 정합 — 회원 모드 일관).
  const guestPassword = isLoggedIn ? undefined : guestCredentials?.password;

  const tableSessionToken = useMemo(
    () => getTableSessionFromStorage()?.token ?? null,
    [],
  );
  // current/bills가 세션 종료 403을 반환하면 Comensal 저장값이 지워진다. 그 전에
  // 현장 결제 요청 시 저장한 Bill/device 단서를 메모리에 붙잡아 영수증을 복구한다.
  const [pendingBillReceipt, setPendingBillReceipt] = useState(readPendingBillReceipt);
  const participant = useComensalSession(tableSessionToken);
  const bills = useSessionBills(tableSessionToken, participant.deviceToken);
  const refreshBills = bills.refresh;
  const [payAll, setPayAll] = useState(false);
  const [isQuoteSyncPending, setIsQuoteSyncPending] = useState(false);
  const [quoteStaleNotice, setQuoteStaleNotice] = useState(false);
  const quoteSyncObservedLoadingRef = useRef(false);
  const ensureParticipantIdentity = participant.ensureIdentity;

  // 저장된 토큰이 있는 두 번째 참여자는 원 주문 비밀번호 없이 current→bills부터 복구한다.
  useEffect(() => {
    if (participant.deviceToken !== null) void ensureParticipantIdentity();
  }, [participant.deviceToken, ensureParticipantIdentity]);

  const joinedCount = bills.data?.participants?.joined_count
    ?? participant.comensal?.session?.joined_count
    ?? 1;
  const activeSplitSession = bills.hasLoaded && bills.isSplitAvailable && bills.data !== null;
  const readOnlySplitSession = bills.hasLoaded
    && bills.data?.has_split_ledger === true
    && !bills.isSplitAvailable;
  const splitSession = activeSplitSession || readOnlySplitSession;
  const splitFromIdentity = participant.comensal?.session?.split_payment_available === true;
  const isJoinedComensal = participant.comensal?.join_status === 'joined';
  // QR start/entry가 저장한 서버 가용성을 첫 렌더부터 소비한다. current가 아직 오지 않은
  // 짧은 구간에도 legacy quote로 빠지면 Invitado가 원 주문 자격증명 경로를 열 수 있다.
  const splitFromStorage = readSplitPaymentAvailable() === true;
  const accessBlocked = (
    splitFromStorage
    || splitFromIdentity
    || activeSplitSession
  ) && !isJoinedComensal;
  const myBillId = splitSession ? (bills.data?.my_bill_id ?? null) : null;
  const hasSplitLedger = bills.data?.has_split_ledger === true;
  const canPayAll = activeSplitSession
    && joinedCount >= 2
    && !hasSplitLedger
    && participant.comensal?.role === 'anfitrion'
    && participant.comensal.join_status === 'joined'
    && (bills.data?.bills.length ?? 0) === 0;
  const shouldPayAll = !activeSplitSession
    || (!hasSplitLedger && joinedCount < 2)
    || (payAll && canPayAll);
  const ownBill = bills.data?.bills.find((bill) => bill.id === myBillId)
    ?? bills.data?.bills.find((bill) =>
      bill.comensal_id === participant.comensal?.id && bill.payment_state === 'en_caja')
    ?? null;
  const isOwnBillProcessing = splitSession
    && !shouldPayAll
    && ownBill?.payment_state === 'en_caja';
  const quoteBillId = activeSplitSession && !shouldPayAll && myBillId !== null
    ? myBillId
    : undefined;
  const quoteEnabled = !accessBlocked
    && !readOnlySplitSession
    && !isOwnBillProcessing
    && (participant.deviceToken === null
    ? true
    : bills.hasLoaded && (shouldPayAll || quoteBillId !== undefined));
  const quoteComensalToken = activeSplitSession && isJoinedComensal
    ? participant.deviceToken ?? undefined
    : undefined;

  useEffect(() => {
    if (!canPayAll && payAll) setPayAll(false);
  }, [canPayAll, payAll]);

  useEffect(() => subscribePendingBillReceipt(() => {
    const next = readPendingBillReceipt();
    // 성공 시 저장소는 지우되 현재 화면의 영수증은 유지한다. 다음 페이지 마운트에는
    // stale context가 없고, 이 페이지에서는 사용자가 방금 낸 영수증을 계속 볼 수 있다.
    if (next !== null) setPendingBillReceipt(next);
  }), []);

  useEffect(() => {
    // en_caja는 정산/영수증 전환을, Bill 0개 대표 화면은 다른 기기의 첫 claim을 따라간다.
    // 첫 개인 Bill이 생기면 canPayAll=false가 되어 대표 polling은 즉시 멈춘다.
    if (!isOwnBillProcessing && !canPayAll) return;
    const timer = window.setInterval(() => { void refreshBills(); }, 5000);
    return () => { window.clearInterval(timer); };
  }, [canPayAll, isOwnBillProcessing, refreshBills]);

  const {
    data,
    isLoading,
    splitPaymentAvailable: legacySplitPaymentAvailable,
    refetch: refetchQuote,
  } = useSessionUnpaidPostpay({
    enabled: quoteEnabled,
    isLoggedIn,
    guestPassword: quoteComensalToken === undefined ? guestPassword : undefined,
    billId: quoteBillId,
    comensalDeviceToken: quoteComensalToken,
  });

  const requestQuoteSync = useCallback((): void => {
    if (!quoteEnabled) return;
    quoteSyncObservedLoadingRef.current = false;
    setIsQuoteSyncPending(true);
    refetchQuote();
  }, [quoteEnabled, refetchQuote]);

  const requestExactQuoteSync = useCallback((): void => {
    if (quoteBillId === undefined) return;
    requestQuoteSync();
  }, [quoteBillId, requestQuoteSync]);

  useEffect(() => {
    if (quoteBillId === undefined || typeof window === 'undefined') return;
    window.addEventListener(SESSION_BILLS_CHANGED_EVENT, requestExactQuoteSync);
    return () => {
      window.removeEventListener(SESSION_BILLS_CHANGED_EVENT, requestExactQuoteSync);
    };
  }, [quoteBillId, requestExactQuoteSync]);

  useEffect(() => {
    if (!isQuoteSyncPending) return;
    if (!quoteEnabled) {
      quoteSyncObservedLoadingRef.current = false;
      setIsQuoteSyncPending(false);
      return;
    }
    if (isLoading) {
      quoteSyncObservedLoadingRef.current = true;
      return;
    }
    if (quoteSyncObservedLoadingRef.current) {
      quoteSyncObservedLoadingRef.current = false;
      setIsQuoteSyncPending(false);
    }
  }, [isLoading, isQuoteSyncPending, quoteEnabled]);

  const handleQuoteStale = useCallback((): void => {
    setQuoteStaleNotice(true);
    void refreshBills();
    requestQuoteSync();
  }, [refreshBills, requestQuoteSync]);
  const splitAvailable = splitSession || splitFromIdentity || legacySplitPaymentAvailable === true;
  const {
    combinations: paymentCombinations,
    loading: isLoadingPaymentCombinations,
    error: paymentCombinationsError,
    retry: retryPaymentCombinations,
  } = usePaymentMethodCombinations();
  const {
    assignments: acceptedPaymentMethods,
    loading: isLoadingAcceptedPaymentMethods,
    error: acceptedPaymentMethodsError,
    retry: retryAcceptedPaymentMethods,
  } = useStoreAcceptedPaymentMethods(data?.storeId ?? null);

  /**
   * 훅의 초기 상태(isLoading=false, data=null — effect 실행 전 첫 렌더)와
   * "조회 완료 후 데이터 없음"(fail-closed redirect 대상)을 구분하는 settle 게이트.
   *
   * 본 effect 는 훅 선언 이후에 선언되어 같은 commit 에서 훅 effect(조회 시작/tokenMissing
   * 결정) **다음**에 실행된다 — settled=true 시점엔 조회 시작(isLoading=true) 또는
   * 토큰 부재/실패 결과가 이미 반영돼 있어, 첫 렌더에서 성급한 redirect 가 발생하지 않는다.
   */
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    setSettled(true);
  }, []);

  const payNowT = payNowTranslations[locale];

  // 패널/헤더 라벨 — 기존 모달과 동일 빌더 공유 (15언어).
  const labels = useMemo(
    () => buildSessionChargeLabels(locale, data?.count ?? 0),
    [locale, data?.count],
  );
  const ownBillHeading = locale === 'es'
    ? {
        title: 'Pagar mi cuenta',
        description: 'Paga únicamente el saldo asignado a tu cuenta.',
      }
    : locale === 'ko'
      ? {
          title: '내 계산서 결제',
          description: '내 계산서에 배정된 금액만 결제합니다.',
        }
      : {
          title: 'Pay my bill',
          description: 'Pay only the amount assigned to your bill.',
        };
  const splitBillNotice = locale === 'es'
    ? {
        refundPolicy: 'Esta cuenta quedará confirmada; cambiar la división después requerirá un reembolso y un nuevo cobro.',
        remainingLabel: 'Saldo de esta cuenta',
      }
    : locale === 'ko'
      ? {
          refundPolicy: '이 계산서는 결제 시 확정됩니다. 이후 분할을 변경하려면 환불 후 다시 결제해야 합니다.',
          remainingLabel: '내 계산서 잔액',
        }
      : {
          refundPolicy: 'This bill will be confirmed when paid. Changing the split afterward requires a refund and a new charge.',
          remainingLabel: 'My bill balance',
        };
  const billTipPolicy = locale === 'es'
    ? 'La solicitud en caja usará el mismo total mostrado, incluida la propina seleccionada. Los puntos no están disponibles.'
    : locale === 'ko'
      ? '현장 결제 요청에도 위에 표시된 동일한 합계와 선택한 팁이 적용됩니다. 포인트는 사용할 수 없습니다.'
      : 'The in-store payment request uses the same total shown above, including the selected tip. Points are unavailable.';
  const chargePageHeading = quoteBillId === undefined ? labels : ownBillHeading;

  const tablePaymentCombinations = useMemo(
    () =>
      computeAvailableCombinations(
        paymentCombinations,
        acceptedPaymentMethods,
        'table',
      ),
    [paymentCombinations, acceptedPaymentMethods],
  );
  const storeAllowsCardPlatformPostpay = useMemo(
    () => tablePaymentCombinations.some((combo) => combo.code === CARD_PLATFORM_POSTPAY_CODE),
    [tablePaymentCombinations],
  );
  const sessionPaymentsAreCardPlatformPostpay = useMemo(
    () =>
      data !== null &&
      data.orders.length > 0 &&
      data.orders.every(
        (order) => order.latestPaymentCombinationCode === CARD_PLATFORM_POSTPAY_CODE,
      ),
    [data],
  );
  // 개인 Bill은 원 주문의 결제 조합을 상속하지 않는다. 서버가 매장 허용 조합으로 Bill
  // payment를 새로 provision하므로, Ana 주문이 cash였어도 Beto는 매장이 허용한 카드를 쓴다.
  const cardEligibleScope = quoteBillId !== undefined || sessionPaymentsAreCardPlatformPostpay;
  const cardChargeAvailable = storeAllowsCardPlatformPostpay && cardEligibleScope;
  const storeAllowsCashManualPostpay = useMemo(
    () => tablePaymentCombinations.some((combo) => combo.code === CASH_MANUAL_POSTPAY_CODE),
    [tablePaymentCombinations],
  );
  const storeAllowsCardPosPostpay = useMemo(
    () => tablePaymentCombinations.some((combo) => combo.code === CARD_POS_POSTPAY_CODE),
    [tablePaymentCombinations],
  );
  const manualPostpayRequestMethods = useMemo(() => {
    const methods: TableSessionPaymentRequestMethod[] = [];
    if (
      storeAllowsCashManualPostpay ||
      data?.orders.some((order) => order.latestPaymentCombinationCode === CASH_MANUAL_POSTPAY_CODE)
    ) {
      methods.push('cash');
    }
    if (
      storeAllowsCardPosPostpay ||
      data?.orders.some((order) => order.latestPaymentCombinationCode === CARD_POS_POSTPAY_CODE)
    ) {
      methods.push('pos_card_reader');
    }

    return methods;
  }, [data, storeAllowsCardPosPostpay, storeAllowsCashManualPostpay]);
  const isResolvingCardChargeAvailability =
    data !== null &&
    cardEligibleScope &&
    (isLoadingPaymentCombinations || isLoadingAcceptedPaymentMethods);
  const cardChargeResolutionFailed =
    cardEligibleScope &&
    !isResolvingCardChargeAvailability &&
    !cardChargeAvailable &&
    manualPostpayRequestMethods.length === 0;

  const handleRetryPaymentMethods = useCallback(() => {
    if (paymentCombinationsError !== null) {
      retryPaymentCombinations();
    }
    if (acceptedPaymentMethodsError !== null) {
      retryAcceptedPaymentMethods();
    }

    // 설정 불일치는 오류 객체 없이 빈 결과로 나타날 수 있으므로 양쪽 계약을 다시 확인한다.
    if (paymentCombinationsError === null && acceptedPaymentMethodsError === null) {
      retryPaymentCombinations();
      retryAcceptedPaymentMethods();
    }
  }, [
    acceptedPaymentMethodsError,
    paymentCombinationsError,
    retryAcceptedPaymentMethods,
    retryPaymentCombinations,
  ]);

  /**
   * challenge 없는 즉시 성공(0원 번들 등) → 주문 목록 refetch + 주문내역으로 replace 복귀.
   *
   * 정상 흐름(3DS challenge redirect)은 페이지를 떠나므로 호출되지 않는다.
   * replace: 주문내역에서 뒤로가기 시 결제 완료된 본 페이지로 되돌아오지 않게 한다
   * (어차피 fail-closed redirect 되지만 히스토리 자체를 남기지 않는 편이 자연스럽다).
   */
  const handleSuccess = useCallback(() => {
    void refetch();
    if (quoteBillId !== undefined) {
      // challenge 없는 Bill 결제도 같은 화면에서 bills→receipt 자동 흐름으로 이어 간다.
      void refreshBills();
      return;
    }
    navigate(ROUTES.ORDER_HISTORY, { replace: true });
  }, [navigate, quoteBillId, refetch, refreshBills]);

  /**
   * 뒤로가기 — 정상 라우트 push 로 진입했으므로 직전 화면(주문내역/메뉴)으로 자연 복귀.
   *
   * codex Important: 직접 URL 진입(새 탭/딥링크 — 앱 내 히스토리 없음)이면 navigate(-1) 이
   * 앱 외부(빈 탭/외부 사이트)로 이탈한다. react-router 는 히스토리 스택의 첫 엔트리를
   * `location.key === 'default'` 로 표식하므로, 이 경우 주문내역으로 replace fallback 한다.
   */
  const handleBack = useCallback(() => {
    if (location.key === 'default') {
      navigate(ROUTES.ORDER_HISTORY, { replace: true });
      return;
    }
    navigate(-1);
  }, [location.key, navigate]);

  // 조회 중(또는 첫 렌더 settle 전) — 로딩 표시 (성급한 fail-closed redirect 차단).
  const isDiscoveringParticipantBills = participant.deviceToken !== null && !bills.hasLoaded;
  if (isLoading || isQuoteSyncPending || isDiscoveringParticipantBills || !settled) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div
          data-testid="session-charge-loading"
          className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"
          role="status"
          aria-label={payNowT.processing}
        />
      </div>
    );
  }

  const hasPendingReceiptForSession = pendingBillReceipt !== null
    && pendingBillReceipt.sessionToken === tableSessionToken;
  const shouldRecoverEndedReceipt = hasPendingReceiptForSession
    && bills.hasLoaded
    && (bills.data === null || !isJoinedComensal);

  if (shouldRecoverEndedReceipt && pendingBillReceipt !== null) {
    return (
      <div data-testid="session-charge-receipt-recovery" className="min-h-screen bg-muted">
        <header className="sticky top-0 z-10 border-b border-border bg-background">
          <div className="mx-auto flex max-w-[600px] items-center gap-2 p-4">
            <button
              type="button"
              onClick={handleBack}
              aria-label={payNowT.session.chargeBack}
              className="-ml-2 rounded p-2 text-muted-foreground"
            >
              <ArrowLeft className="h-6 w-6" aria-hidden="true" />
            </button>
            <h1 className="font-semibold text-foreground">
              {locale === 'es' ? 'Estado de mi pago' : locale === 'ko' ? '내 결제 상태' : 'My payment status'}
            </h1>
          </div>
        </header>
        <main className="mx-auto max-w-[600px] space-y-4 p-4">
          <p className="text-sm text-muted-foreground" role="status">
            {locale === 'es'
              ? 'La sesión terminó. Estamos recuperando el recibo de tu cuenta.'
              : locale === 'ko'
                ? '세션이 종료되었습니다. 내 계산서 영수증을 불러오고 있습니다.'
                : 'The session ended. We are recovering your bill receipt.'}
          </p>
          <BillReceiptViewer
            sessionToken={pendingBillReceipt.sessionToken}
            deviceToken={pendingBillReceipt.deviceToken}
            bills={[]}
            comensalId={participant.comensal?.id ?? -1}
            autoOpenBillId={pendingBillReceipt.billId}
          />
        </main>
      </div>
    );
  }

  if (accessBlocked) {
    return (
      <div data-testid="session-charge-access-only" className="min-h-screen bg-muted">
        <main className="mx-auto max-w-[600px] p-4">
          <ComensalAccessPanel
            sessionToken={tableSessionToken}
            isAvailable
            labels={getComensalSessionLabels(locale)}
            state={participant}
          />
        </main>
      </div>
    );
  }

  if (
    isOwnBillProcessing
    && tableSessionToken !== null
    && participant.deviceToken !== null
    && participant.comensal !== null
  ) {
    return (
      <div data-testid="session-bill-processing" className="min-h-screen bg-muted">
        <header className="sticky top-0 z-10 border-b border-border bg-background">
          <div className="mx-auto flex max-w-[600px] items-center gap-2 p-4">
            <button
              type="button"
              onClick={handleBack}
              aria-label={payNowT.session.chargeBack}
              className="-ml-2 rounded p-2 text-muted-foreground"
            >
              <ArrowLeft className="h-6 w-6" aria-hidden="true" />
            </button>
            <h1 className="font-semibold text-foreground">
              {locale === 'es' ? 'Pago en proceso' : locale === 'ko' ? '결제 처리 중' : 'Payment processing'}
            </h1>
          </div>
        </header>
        <main className="mx-auto max-w-[600px] space-y-4 p-4">
          <section className="rounded-lg border border-border bg-card p-4">
            <p className="text-sm text-foreground" role="status">
              {locale === 'es'
                ? 'Tu solicitud ya está en caja. No inicies otro pago mientras se confirma.'
                : locale === 'ko'
                  ? '현장 결제 요청을 처리 중입니다. 확정될 때까지 새 결제를 시작하지 마세요.'
                  : 'Your request is already being processed. Do not start another payment while it is confirmed.'}
            </p>
            <button
              type="button"
              onClick={() => { void refreshBills(); }}
              disabled={bills.isLoading}
              className="mt-3 w-full rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50"
              data-testid="session-bill-processing-refresh"
            >
              {locale === 'es' ? 'Actualizar estado' : locale === 'ko' ? '상태 새로고침' : 'Refresh status'}
            </button>
          </section>
          {hasPendingReceiptForSession && pendingBillReceipt !== null ? (
            <BillReceiptViewer
              sessionToken={pendingBillReceipt.sessionToken}
              deviceToken={pendingBillReceipt.deviceToken}
              bills={bills.data?.bills ?? []}
              comensalId={participant.comensal.id}
              autoOpenBillId={pendingBillReceipt.billId}
            />
          ) : null}
        </main>
      </div>
    );
  }

  if (readOnlySplitSession) {
    return (
      <div data-testid="session-charge-split-readonly" className="min-h-screen bg-muted">
        <header className="sticky top-0 z-10 border-b border-border bg-background">
          <div className="mx-auto flex max-w-[600px] items-center gap-2 p-4">
            <button
              type="button"
              onClick={handleBack}
              aria-label={payNowT.session.chargeBack}
              className="-ml-2 rounded p-2 text-muted-foreground"
            >
              <ArrowLeft className="h-6 w-6" aria-hidden="true" />
            </button>
            <h1 className="font-semibold text-foreground">
              {locale === 'es' ? 'Estado de las cuentas' : locale === 'ko' ? '계산서 상태' : 'Bill status'}
            </h1>
          </div>
        </header>
        <main className="mx-auto max-w-[600px] space-y-4 p-4">
          <p
            className="rounded-lg border border-border bg-card p-4 text-sm text-foreground"
            role="status"
            data-testid="split-readonly-staff-notice"
          >
            {locale === 'es'
              ? 'La división está desactivada. Puedes consultar las cuentas existentes, pero pide al personal que complete el cobro en caja.'
              : locale === 'ko'
                ? '분할 결제가 중지되었습니다. 기존 계산서는 확인만 가능하므로 직원에게 현장 결제를 요청해 주세요.'
                : 'Split payment is disabled. You can review existing bills, but ask staff to complete payment at the register.'}
          </p>
          <SplitPaymentSection
            sessionToken={tableSessionToken}
            isAvailable={false}
            readOnly
            currency={currency}
            labels={getSplitPaymentLabels(locale)}
            claimLabels={getSplitClaimLabels(locale)}
            participantState={participant}
            billsState={bills}
            autoOpenBillId={hasPendingReceiptForSession
              ? pendingBillReceipt?.billId
              : undefined}
          />
        </main>
      </div>
    );
  }

  // fail-closed: 미결제 없음 / 빈 라인(계약 위반) / 토큰 부재 / 조회 실패 → 주문내역 복귀.
  // 시트 CTA·배너·openCharge 와 동일한 SSOT(hasPayableSessionLines) 판정 — 직접 URL 진입 방어.
  if (!data || !hasPayableSessionLines(data)) {
    if (splitAvailable) {
      return (
        <div data-testid="session-charge-split-entry" className="min-h-screen bg-muted">
          <header className="sticky top-0 z-10 border-b border-border bg-background">
            <div className="mx-auto flex max-w-[600px] items-center gap-2 p-4">
              <button
                type="button"
                onClick={handleBack}
                aria-label={payNowT.session.chargeBack}
                className="-ml-2 rounded p-2 text-muted-foreground"
              >
                <ArrowLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <h1 className="font-semibold text-foreground">{labels.title}</h1>
            </div>
          </header>
          <main className="mx-auto max-w-[600px] space-y-4 p-4">
            <ComensalAccessPanel
              sessionToken={tableSessionToken}
              isAvailable
              labels={getComensalSessionLabels(locale)}
              state={participant}
            />
            <SplitPaymentSection
              sessionToken={tableSessionToken}
              isAvailable
              currency={currency}
              labels={getSplitPaymentLabels(locale)}
              claimLabels={getSplitClaimLabels(locale)}
              participantState={participant}
              billsState={bills}
              autoOpenBillId={hasPendingReceiptForSession
                ? pendingBillReceipt?.billId
                : undefined}
            />
            {canPayAll ? (
              <button
                type="button"
                onClick={() => { setPayAll(true); }}
                className="w-full rounded-md border border-primary px-4 py-3 font-semibold text-primary"
                data-testid="pay-all-session"
              >
                {locale === 'es' ? 'Yo pago todo' : locale === 'ko' ? '내가 전부 결제' : 'I will pay all'}
              </button>
            ) : null}
          </main>
        </div>
      );
    }
    return <Navigate to={ROUTES.ORDER_HISTORY} replace />;
  }

  // 서버 권위 합계 — 파싱 불가 시 0(표시 안전망). FE 재합산으로 대체하지 않는다.
  const numericTotal = Number(data.totalAmount);
  const totalDisplay = formatCurrency(
    Number.isFinite(numericTotal) ? numericTotal : 0,
    currency,
  );

  // 1건이면 소계(=합계 중복) 생략 — 시트의 "1건 평면 나열" 정책과 동일한 시각 단순화.
  const showOrderSubtotal = data.orders.length > 1;

  return (
    <div data-testid="session-charge-page" className="min-h-screen bg-muted">
      {/* 헤더 — 뒤로가기 + 제목/설명 */}
      <header className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="max-w-[600px] mx-auto flex items-center gap-2 p-4">
          <button
            type="button"
            data-testid="session-charge-back"
            onClick={handleBack}
            aria-label={payNowT.session.chargeBack}
            className="p-2 -ml-2 text-muted-foreground hover:text-foreground rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ArrowLeft className="w-6 h-6" aria-hidden="true" />
          </button>
          <div>
            <h1 className="font-semibold text-foreground">{chargePageHeading.title}</h1>
            <p className="text-sm text-muted-foreground">{chargePageHeading.description}</p>
          </div>
        </div>
      </header>

      <main className="max-w-[600px] mx-auto p-4 pb-[96px] space-y-4">
        <ComensalAccessPanel
          sessionToken={data.token}
          isAvailable={splitAvailable}
          labels={getComensalSessionLabels(locale)}
          state={participant}
        />
        {/*
          분할결제(#814) — **매장이 쓰지 않으면 통째로 사라진다**. 조회가 403 이면
          `isSplitAvailable=false` 가 되고 이 섹션은 아무것도 그리지 않는다. 주문 내역
          위에 두는 이유는, 누가 얼마를 냈는지 보고 나서 자기 몫을 정하기 때문이다.
        */}
        <SplitPaymentSection
          sessionToken={data.token}
          isAvailable={splitAvailable}
          currency={currency}
          labels={getSplitPaymentLabels(locale)}
          claimLabels={getSplitClaimLabels(locale)}
          participantState={participant}
          billsState={bills}
          autoOpenBillId={hasPendingReceiptForSession
            ? pendingBillReceipt?.billId
            : undefined}
        />

        {quoteStaleNotice ? (
          <p
            className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            role="alert"
            data-testid="bill-quote-stale-notice"
          >
            {locale === 'es'
              ? 'El monto cambió. Revisa el saldo actualizado antes de volver a pagar.'
              : locale === 'ko'
                ? '금액이 변경되었습니다. 다시 결제하기 전에 갱신된 잔액을 확인해 주세요.'
                : 'The amount changed. Review the updated balance before trying again.'}
          </p>
        ) : null}

        {splitSession && joinedCount >= 2 ? (
          <section className="space-y-2" aria-label="Payment scope">
            <div className={canPayAll ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-1'}>
              <button
                type="button"
                onClick={() => { setPayAll(false); }}
                disabled={myBillId === null}
                aria-pressed={!shouldPayAll}
                className={!shouldPayAll
                  ? 'rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground'
                  : 'rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50'}
                data-testid="pay-my-bill"
              >
                {locale === 'es' ? 'Pagar mi cuenta' : locale === 'ko' ? '내 계산서 결제' : 'Pay my bill'}
              </button>
              {canPayAll ? (
                <button
                  type="button"
                  onClick={() => { setPayAll(true); }}
                  aria-pressed={shouldPayAll}
                  className={shouldPayAll
                    ? 'rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground'
                    : 'rounded-md border border-border px-3 py-2 text-sm'}
                  data-testid="pay-all-session"
                >
                  {locale === 'es' ? 'Yo pago todo' : locale === 'ko' ? '내가 전부 결제' : 'I will pay all'}
                </button>
              ) : null}
            </div>
            {participant.comensal?.role === 'anfitrion' && !canPayAll ? (
              <p className="text-xs text-muted-foreground" data-testid="pay-all-unavailable">
                {locale === 'es'
                  ? 'Primero deben liquidarse las cuentas que ya se dividieron.'
                  : locale === 'ko'
                    ? '이미 나눈 계산서를 먼저 정산해 주세요.'
                    : 'The bills already split out must be settled first.'}
              </p>
            ) : null}
          </section>
        ) : null}

        {/* 주문한 메뉴 내역 — 주문별 섹션 (주문번호 + 라인 + 소계). 데이터는 #654 orders 구조. */}
        <section
          data-testid="session-charge-orders"
          aria-label={payNowT.session.sheetTitle}
          className="rounded-2xl bg-background p-4 space-y-4"
        >
          <h2 className="font-semibold text-foreground">{payNowT.session.sheetTitle}</h2>

          {data.orders.map((order) => (
            <section
              key={order.id}
              data-testid={`session-charge-order-${order.id}`}
              aria-label={order.orderNumber ?? `#${order.id}`}
            >
              {/* 주문번호 라벨 — 서버 order_number 그대로 (i18n 불요). null 이면 #id 폴백. */}
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {order.orderNumber ?? `#${order.id}`}
              </p>
              <ul className="space-y-3">
                {order.items.map((item) => (
                  <SessionOrderLine key={item.id} item={item} currency={currency} />
                ))}
              </ul>
              {/* 주문별 소계 — 서버 권위 order.total. 비정상(null)이면 표시 생략(FE 재합산 금지). */}
              {showOrderSubtotal && order.total !== null && (
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/60">
                  <span className="text-xs text-muted-foreground">
                    {payNowT.session.orderSubtotal}
                  </span>
                  <span
                    data-testid={`session-charge-order-subtotal-${order.id}`}
                    className="text-sm text-foreground tabular-nums"
                  >
                    {formatCurrency(order.total, currency)}
                  </span>
                </div>
              )}
            </section>
          ))}

          {/* 합계 (서버 권위 total_amount) */}
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm font-medium text-foreground">
              {payNowT.session.sheetTotalLabel}
            </span>
            <span
              data-testid="session-charge-total"
              className="text-base font-semibold text-foreground tabular-nums"
            >
              {totalDisplay}
            </span>
          </div>
        </section>

        {/* 결제 패널 — 팁/포인트(#654 단건 게이팅) + 0원 분기 + 카드 폼 + 3DS challenge redirect */}
        <div className="rounded-2xl bg-background p-4">
          {cardChargeResolutionFailed ? (
            <section
              data-testid="session-card-availability-error"
              role="alert"
              className="rounded-[10px] border border-destructive/20 bg-destructive/5 p-[14px] space-y-[10px]"
            >
              <p className="text-[13px] leading-5 text-destructive">
                {labels.paymentMethodResolutionError}
              </p>
              <button
                type="button"
                data-testid="session-card-availability-retry"
                onClick={handleRetryPaymentMethods}
                className="h-[44px] w-full rounded-[8px] bg-primary px-[14px] text-[15px] font-semibold text-white transition-colors hover:bg-primary-hover"
              >
                {paymentTranslations[locale].tryAgain}
              </button>
            </section>
          ) : (
            <SessionChargePanel
              token={data.token}
              totalAmount={data.totalAmount}
              subtotalAmount={data.subtotalAmount}
              initialTipAmount={data.tipAmount}
              expectedBillSubtotalMinor={data.expectedBillSubtotalMinor}
              expectedTotalMinor={data.expectedTotalMinor}
              count={data.count}
              adjustmentOrderId={data.adjustmentOrderId}
              storeId={data.storeId}
              isMember={isLoggedIn}
              guestPassword={quoteComensalToken === undefined ? guestPassword : undefined}
              billId={quoteBillId}
              comensalDeviceToken={quoteComensalToken}
              bundleTipEnabled
              cardChargeAvailable={cardChargeAvailable}
              manualPostpayRequestMethods={manualPostpayRequestMethods}
              isResolvingCardChargeAvailability={isResolvingCardChargeAvailability}
              labels={{
                ...labels,
                manualPostpayNotice: labels.manualPostpayNotice
                  ? {
                      ...labels.manualPostpayNotice,
                      billTipPolicy,
                    }
                  : undefined,
                splitBillNotice,
              }}
              onSuccess={handleSuccess}
              onQuoteStale={handleQuoteStale}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default SessionChargePage;
